(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-wbu-shop-the-look]';

  /*
   * Prevent duplicate global initialization if the asset
   * is loaded more than once on the same page.
   */
  if (window.WBUShopTheLook) {
    window.WBUShopTheLook.initAll();
    return;
  }

  const instances = new Set();

  const isMobile = () => {
    return window.matchMedia('(max-width: 989px)').matches;
  };

  const init = (root) => {
    if (!root || root.dataset.wbuShopTheLookInitialized === 'true') {
      return;
    }

    root.dataset.wbuShopTheLookInitialized = 'true';

    const hotspots = Array.from(
      root.querySelectorAll('[data-wbu-hotspot]')
    );

    const hotspotWrappers = Array.from(
      root.querySelectorAll('[data-wbu-hotspot-wrapper]')
    );

    const productCards = Array.from(
      root.querySelectorAll('[data-wbu-product-card]')
    );

    if (!hotspots.length || !productCards.length) {
      return;
    }

    let activeIndex = null;

    const getHotspotWrapper = (index) => {
      return hotspotWrappers.find(
        (wrapper) => wrapper.dataset.productIndex === String(index)
      );
    };

    const getProductCard = (index) => {
      return productCards.find(
        (card) => card.dataset.productIndex === String(index)
      );
    };

    const clear = () => {
      activeIndex = null;

      hotspots.forEach((hotspot) => {
        hotspot.setAttribute('aria-current', 'false');
      });

      hotspotWrappers.forEach((wrapper) => {
        wrapper.classList.remove('is-active');

        const tooltip = wrapper.querySelector('[data-wbu-tooltip]');

        if (tooltip) {
          tooltip.setAttribute('aria-hidden', 'true');
        }
      });

      productCards.forEach((card) => {
        card.setAttribute('aria-current', 'false');
      });
    };

    const activate = (
      index,
      {
        showTooltip = false,
        scrollToCard = false
      } = {}
    ) => {
      const productIndex = String(index);

      activeIndex = productIndex;

      hotspots.forEach((hotspot) => {
        const isActive =
          hotspot.dataset.productIndex === productIndex;

        hotspot.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );
      });

      hotspotWrappers.forEach((wrapper) => {
        const isActive =
          wrapper.dataset.productIndex === productIndex;

        wrapper.classList.toggle(
          'is-active',
          isActive && showTooltip
        );

        const tooltip = wrapper.querySelector('[data-wbu-tooltip]');

        if (tooltip) {
          tooltip.setAttribute(
            'aria-hidden',
            isActive && showTooltip ? 'false' : 'true'
          );
        }
      });

      productCards.forEach((card) => {
        const isActive =
          card.dataset.productIndex === productIndex;

        card.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );
      });

      /*
       * Mobile:
       * no modal and no floating tooltip.
       * Bring the corresponding product card into view.
       */
      if (scrollToCard && isMobile()) {
        const activeCard = getProductCard(productIndex);

        if (activeCard) {
          window.setTimeout(() => {
            activeCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }, 120);
        }
      }
    };

    /*
     * HOTSPOT INTERACTIONS
     */
    hotspots.forEach((hotspot) => {
      const productIndex = hotspot.dataset.productIndex;

      hotspot.addEventListener('click', (event) => {
        event.stopPropagation();

        const wrapper = getHotspotWrapper(productIndex);

        const tooltipIsOpen =
          wrapper && wrapper.classList.contains('is-active');

        /*
         * Second click on the same open hotspot closes it.
         */
        if (
          activeIndex === productIndex &&
          tooltipIsOpen &&
          !isMobile()
        ) {
          clear();
          return;
        }

        activate(productIndex, {
          showTooltip: !isMobile(),
          scrollToCard: isMobile()
        });
      });

      /*
       * Keyboard users receive the same desktop information.
       */
      hotspot.addEventListener('focus', () => {
        if (!isMobile()) {
          activate(productIndex, {
            showTooltip: true
          });
        }
      });

      hotspot.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          clear();
          hotspot.blur();
        }
      });
    });

    /*
     * PRODUCT CARD -> HOTSPOT SYNCHRONIZATION
     *
     * Hovering or keyboard-focusing a product card
     * highlights its corresponding hotspot.
     */
    productCards.forEach((card) => {
      const productIndex = card.dataset.productIndex;

      card.addEventListener('mouseenter', () => {
        if (!isMobile()) {
          activate(productIndex, {
            showTooltip: false
          });
        }
      });

      card.addEventListener('focusin', () => {
        activate(productIndex, {
          showTooltip: false
        });
      });
    });

    /*
     * Clicking empty space inside the component clears
     * the current selection.
     */
    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-wbu-hotspot-wrapper]')) {
        return;
      }

      if (event.target.closest('[data-wbu-product-card]')) {
        return;
      }

      clear();
    });

    instances.add({
      root,
      clear
    });
  };

  const initAll = (scope = document) => {
    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      init(scope);
    }

    const roots = scope.querySelectorAll
      ? scope.querySelectorAll(ROOT_SELECTOR)
      : [];

    roots.forEach((root) => {
      init(root);
    });
  };

  const clearAll = () => {
    instances.forEach((instance) => {
      /*
       * Ignore sections that Shopify has already removed
       * from the DOM during Theme Editor updates.
       */
      if (!document.documentElement.contains(instance.root)) {
        instances.delete(instance);
        return;
      }

      instance.clear();
    });
  };

  window.WBUShopTheLook = {
    initAll,
    clearAll
  };

  /*
   * Normal storefront initialization.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll(),
      { once: true }
    );
  } else {
    initAll();
  }

  /*
   * Close tooltip when clicking outside the WBU module.
   */
  document.addEventListener('click', (event) => {
    const clickedInsideShopTheLook =
      event.target.closest(ROOT_SELECTOR);

    if (!clickedInsideShopTheLook) {
      clearAll();
    }
  });

  /*
   * Global Escape support.
   */
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      clearAll();
    }
  });

  /*
   * Shopify Theme Editor:
   * initialize the section again when Shopify reloads it.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );
})();